import setInnerHTML from '@helpers/dom/setInnerHTML';
import {Middleware} from '@helpers/middleware';
import wrapEmojiText from '@lib/richTextProcessor/wrapEmojiText';
import {avatarNew} from '@components/avatarNew';
import PeerTitle from '@components/peerTitle';
import Scrollable from '@components/scrollable';
import AutocompleteHelper from '@components/chat/autocompleteHelper';
import AutocompleteHelperController from '@components/chat/autocompleteHelperController';
import Icon from '@components/icon';
import I18n, {LangPackKey} from '@lib/langPack';

export default class AutocompletePeerHelper extends AutocompleteHelper {
  protected static BASE_CLASS = 'autocomplete-peer-helper';
  protected static BASE_CLASS_LIST_ELEMENT = AutocompletePeerHelper.BASE_CLASS + '-list-element';
  private scrollable: Scrollable;

  constructor(
    appendTo: HTMLElement,
    controller: AutocompleteHelperController,
    protected className: string,
    onSelect: (target: Element) => boolean | void,
    ariaLabel: LangPackKey
  ) {
    super({
      appendTo,
      controller,
      listType: 'y',
      onSelect
    });

    this.container.classList.add(AutocompletePeerHelper.BASE_CLASS, className);
    this.container.setAttribute('role', 'listbox');
    this.container.setAttribute('aria-label', I18n.format(ariaLabel, true));
  }

  public init() {
    this.list = this.container.ownerDocument.createElement('div');
    this.list.classList.add(AutocompletePeerHelper.BASE_CLASS + '-list', this.className + '-list');

    this.container.append(this.list);

    this.scrollable = new Scrollable(this.container);

    this.addEventListener('visible', () => {
      setTimeout(() => { // it is not rendered yet
        this.scrollable.scrollPosition = 0;
      }, 0);
    });
  }

  public render(
    data: {peerId: PeerId, name?: string, description?: string, ephemeral?: boolean}[],
    middleware: Middleware,
    doNotShow?: boolean
  ) {
    if(this.init) {
      if(!data.length) {
        return;
      }

      this.init();
      this.init = null;
    }

    if(data.length) {
      this.list.replaceChildren();
      data.forEach((d) => {
        const div = AutocompletePeerHelper.listElement({
          className: this.className,
          peerId: d.peerId,
          name: d.name,
          description: d.description,
          ephemeral: d.ephemeral,
          middleware,
          ownerDocument: this.container.ownerDocument
        });

        this.list.append(div);
      });
    }

    if(!doNotShow) {
      this.toggle(!data.length);
    }
  }

  public static listElement(options: {
    className: string,
    peerId: PeerId,
    name?: string,
    description?: string,
    ephemeral?: boolean,
    middleware: Middleware,
    ownerDocument?: Document
  }) {
    const BASE = AutocompletePeerHelper.BASE_CLASS_LIST_ELEMENT;
    options.className += '-list-element';
    const ownerDocument = options.ownerDocument || document;

    const div = ownerDocument.createElement('div');
    div.classList.add(BASE, options.className);
    div.dataset.peerId = '' + options.peerId;
    div.setAttribute('role', 'option');
    div.setAttribute('aria-selected', 'false');
    if(options.ephemeral) {
      div.dataset.ephemeral = '1';
    }

    const {node} = avatarNew({
      middleware: options.middleware,
      isBig: false,
      size: 30,
      peerId: options.peerId
    });
    node.classList.add(BASE + '-avatar', options.className + '-avatar');

    const name = ownerDocument.createElement('div');
    name.classList.add(BASE + '-name', options.className + '-name');
    if(!options.name) {
      name.append(new PeerTitle({
        peerId: options.peerId,
        dialog: false,
        onlyFirstName: false,
        plainText: false
      }).element);
    } else {
      setInnerHTML(name, wrapEmojiText(options.name));
    }

    div.append(node, name);

    if(options.ephemeral) {
      const ephemeral = Icon('eyecross', BASE + '-ephemeral', options.className + '-ephemeral');
      const tooltip = I18n.format('Ephemeral.CommandTooltip', true);
      ephemeral.title = tooltip;
      ephemeral.setAttribute('aria-label', tooltip);
      div.append(ephemeral);
    }

    if(options.description) {
      const description = ownerDocument.createElement('div');
      description.classList.add(BASE + '-description', options.className + '-description');
      setInnerHTML(description, wrapEmojiText(options.description));
      div.append(description);
    }

    return div;
  }
}
